import { Outlet, Link, createRootRoute, useRouterState } from "@tanstack/react-router";
import { AppSidebar } from "@/components/dashboard/AppSidebar";
import { ThemeProvider } from "@/components/dashboard/ThemeProvider";
import { AuthProvider } from "@/lib/auth-context";
import { AuthGate } from "@/components/auth/AuthGate";
import { NotificationsProvider } from "@/lib/notifications-context";
import { Toaster } from "@/components/ui/sonner";
import { DashboardLayoutProvider } from "@/lib/dashboard-layout";
import { DashboardLiveProvider } from "@/lib/dashboard-live";
import { PRODUCT_HOME } from "@/lib/product";
import { isAdminPathname, normalizeAppPathname } from "@/lib/route-path";
import { SupportChatWidget } from "@/components/support/SupportChatWidget";

function NotFoundComponent() {
  return (
    <div className="genrl-route-canvas flex min-h-screen items-center justify-center px-4">
      <div className="max-w-md rounded-[28px] border border-[var(--genrl-panel-border)] bg-[var(--genrl-panel-bg)] p-8 text-center shadow-[var(--genrl-panel-shadow)] backdrop-blur-2xl">
        <h1 className="text-7xl font-bold text-[var(--genrl-panel-ink)]">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-[var(--genrl-panel-ink)]">Page not found</h2>
        <p className="mt-2 text-sm text-[var(--genrl-panel-ink-soft)]">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to={PRODUCT_HOME}
            className="inline-flex min-h-10 items-center justify-center rounded-full px-5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
            style={{ background: "var(--genrl-panel-accent)" }}
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

const CHROMELESS_ROUTES = new Set([
  "/",
  "/alliancedao",
  "/auth/callback",
  "/administrator",
  "/device",
  "/early-access",
  "/email-verification",
  "/login",
  "/onboarding",
  "/password-reset",
  "/privacy",
  "/request-access",
  "/signup",
  "/terms-and-conditions",
  "/wallet",
]);

function isChromelessRoute(pathname: string) {
  const normalized = normalizeAppPathname(pathname);
  return CHROMELESS_ROUTES.has(normalized) || isAdminPathname(normalized);
}

function RootComponent() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const routePathname = normalizeAppPathname(pathname);
  const chromeless = isChromelessRoute(routePathname);
  const walletRoute = routePathname === "/early-access" || routePathname === "/wallet";
  const adminRoute = isAdminPathname(routePathname);
  const canvasClassName = adminRoute ? "admin-route-canvas" : walletRoute ? "wallet-route-canvas" : "genrl-route-canvas";
  const routeContent = chromeless ? (
    <div className={`${canvasClassName} min-h-screen w-full`}>
      <Outlet />
    </div>
  ) : (
    <DashboardLiveProvider>
      <div className="genrl-app-frame flex min-h-screen w-full">
        <AppSidebar />
        <div className="genrl-content-frame flex min-w-0 flex-1 flex-col">
          <Outlet />
        </div>
      </div>
    </DashboardLiveProvider>
  );

  if (adminRoute) {
    return (
      <ThemeProvider>
        {routeContent}
        <Toaster />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <AuthProvider>
        <NotificationsProvider>
          <DashboardLayoutProvider>
            <AuthGate>{routeContent}</AuthGate>
          </DashboardLayoutProvider>
          {!adminRoute && routePathname !== "/alliancedao" ? <SupportChatWidget /> : null}
          <Toaster />
        </NotificationsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
